import React, { useState, useEffect } from 'react';
import './RefundPolicy.css';
import { motion } from 'framer-motion';
import {
    FaUndoAlt, FaHandshake, FaExclamationTriangle, FaRegClock,
    FaBalanceScale, FaEnvelope,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';
import useSEO, { SITE_URL } from '../utils/useSEO';

const fadeInUp = {
    hidden:   { opacity: 0, y: 30 },
    visible:  { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const getIcon = (type) => {
    switch (type) {
        case 'undo':    return <FaUndoAlt />;
        case 'shake':   return <FaHandshake />;
        case 'warning': return <FaExclamationTriangle />;
        case 'clock':   return <FaRegClock />;
        case 'balance': return <FaBalanceScale />;
        case 'email':   return <FaEnvelope />;
        default:        return <FaUndoAlt />;
    }
};

/* ── Default content shown when the API has no data ── */
const DEFAULT_SECTIONS = [
    {
        icon: 'shake',
        title: 'Nature of Our Services',
        content:
            'Nimai Solutions provides custom software development, mobile app development, UI/UX design, cloud services, and digital marketing. All deliverables are tailor-made, time-intensive, and irreversibly customised to each client. Due to this bespoke nature, our refund policy is governed by the project agreement signed prior to commencement of work.',
        points: [
            'Each project is scoped and priced specifically for the client\'s requirements.',
            'Work begins only after mutual agreement on scope, timeline, and payment terms.',
            'Completed milestones represent permanent transfer of intellectual effort and cannot be reclaimed.',
        ],
    },
    {
        icon: 'undo',
        title: 'Refund Eligibility',
        content:
            'Refunds are considered in limited, clearly defined circumstances. By default, payments made for services are non-refundable once work has commenced. However, we are committed to fairness and will evaluate each situation on its merits.',
        points: [
            'If Nimai Solutions fails to deliver the agreed scope within the contract timeline without valid cause, a prorated refund may be issued for undelivered work.',
            'Advance payments for work not yet started are refundable in full upon written cancellation within 48 hours of payment.',
            'Payments for completed and client-approved milestones are non-refundable under any circumstances.',
            'Monthly retainer or maintenance fees are non-refundable once a billing cycle has commenced.',
        ],
    },
    {
        icon: 'clock',
        title: 'Project Cancellation Policy',
        content:
            'Either party may terminate the project by providing written notice. The following conditions apply upon cancellation.',
        points: [
            'Client-initiated cancellation: The client is liable to pay for all work completed up to the cancellation date. Any advance beyond work done will be refunded within 15 business days.',
            'Nimai Solutions-initiated cancellation: If we terminate a project without valid cause, the client will receive a full refund for any undelivered milestones paid in advance.',
            'Cancellation due to scope disputes must be accompanied by a formal written dispute filed within 7 days of the disagreement.',
            'No cancellation fee is charged; only billable work up to the termination date is due.',
        ],
    },
    {
        icon: 'warning',
        title: 'Non-Refundable Scenarios',
        content:
            'The following situations explicitly do not qualify for a refund, regardless of outcome.',
        points: [
            'Change of mind or business direction after project commencement.',
            'Delays caused by the client — including delayed feedback, content provision, or approvals.',
            'Dissatisfaction with design aesthetics after the client approved mockups or wireframes.',
            'Third-party service failures (hosting, domain, payment gateways, APIs) outside our control.',
            'Projects paused or abandoned by the client for more than 30 days without prior agreement.',
            'Post-launch support requests beyond the warranty period defined in the contract.',
        ],
    },
    {
        icon: 'balance',
        title: 'Dispute Resolution',
        content:
            'We believe most disputes arise from miscommunication and can be resolved amicably. If you are unhappy with any aspect of our service, please follow the escalation process below before requesting a refund.',
        points: [
            'Step 1 — Contact your assigned project manager directly. Most issues are resolved within 48 hours.',
            'Step 2 — If unresolved, email us at hello@nimaisolutions.in with full details of your concern.',
            'Step 3 — Our management team will review and respond within 5 business days.',
            'Step 4 — If still unresolved, both parties agree to engage a neutral third-party mediator before any legal action.',
            'All disputes are governed by the laws of Gujarat, India, and subject to the jurisdiction of Vadodara courts.',
        ],
    },
    {
        icon: 'email',
        title: 'How to Request a Refund',
        content:
            'If you believe you qualify for a refund under this policy, please reach out to us through the following process. We aim to process all valid refund requests within 15 business days.',
        points: [
            'Email hello@nimaisolutions.in with the subject line: "Refund Request — [Your Project Name]".',
            'Include your invoice number, the amount paid, the date of payment, and a brief reason for the refund.',
            'Our team will acknowledge your request within 2 business days.',
            'Approved refunds are processed via the original payment method.',
            'Bank transfer refunds may take 7–10 additional business days to reflect in your account.',
        ],
    },
];

const RefundPolicy = () => {
    useSEO({
        title:       'Refund Policy | Nimai Solutions',
        description: 'Read Nimai Solutions\' refund and cancellation policy. Understand our terms for project payments, refund eligibility, and dispute resolution.',
        canonical:   `${SITE_URL}/refund-policy`,
        keywords:    'Nimai Solutions refund policy, software development refund, cancellation policy, project payment terms',
    });

    const [refund,     setRefund]     = useState(null);
    const [isLoading,  setIsLoading]  = useState(true);

    useEffect(() => {
        const fetchRefund = async () => {
            try {
                const { ok, data: result } = await safeFetch(
                    'https://aayushrealproperties.in/nimaibackend/public/api/refund-policies'
                );
                let item = null;
                if (Array.isArray(result))          item = result[0];
                else if (result?.data)              item = Array.isArray(result.data) ? result.data[0] : result.data;
                if (ok && item) setRefund(item);
            } catch (_) { /* silently fall back to defaults */ }
            finally { setIsLoading(false); }
        };
        fetchRefund();
    }, []);

    if (isLoading) {
        return (
            <div className="refund-loading">
                <div className="loader" />
            </div>
        );
    }

    /* Merge API data with defaults */
    const heroTitle  = refund?.hero_title_part1       || 'Our';
    const heroHl     = refund?.hero_highlight_word    || 'Refund';
    const heroSuffix = refund?.hero_title_suffix      || 'Policy';
    const heroDesc   = refund?.hero_description       ||
        'We are committed to fair and transparent billing. Read our complete refund and cancellation policy before engaging our services.';
    const lastUpdated = refund?.last_updated_date     || 'June 1, 2026';
    const sections   = (refund?.sections?.length > 0 ? refund.sections : DEFAULT_SECTIONS).map(s => ({
        icon:    s.icon_type  || s.icon,
        title:   s.section_title || s.title,
        content: s.content,
        points:  s.key_points   || s.points || [],
    }));

    return (
        <div className="refund-container">
            <Navbar />

            {/* ── Hero ── */}
            <section className="refund-hero">
                <div className="refund-hero-bg-shape" />
                <motion.div
                    className="refund-hero-content"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="refund-eyebrow">LEGAL / BILLING</span>
                    <h1>
                        {heroTitle} <span>{heroHl}</span> {heroSuffix}
                    </h1>
                    <p>{heroDesc}</p>
                </motion.div>
            </section>

            {/* ── Content ── */}
            <div className="refund-content-wrapper">
                <motion.div
                    className="refund-card"
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    {sections.map((section, index) => (
                        <div key={index} className="refund-section">
                            <h2>
                                {getIcon(section.icon)}
                                {index + 1}. {section.title}
                            </h2>
                            <p>{section.content}</p>
                            {section.points?.length > 0 && (
                                <ul className="refund-list">
                                    {section.points.map((point, i) => (
                                        <li key={i}>{point}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}

                    {/* ── Highlight box ── */}
                    <div className="refund-highlight-box">
                        <FaHandshake className="refund-highlight-icon" />
                        <div>
                            <strong>Questions about a payment?</strong>
                            <p>
                                Email us at{' '}
                                <a href="mailto:hello@nimaisolutions.in">hello@nimaisolutions.in</a>
                                {' '}and we will get back to you within 2 business days.
                            </p>
                        </div>
                    </div>

                    <div className="last-updated">
                        <span>Last Updated: {lastUpdated}</span>
                        <span
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            style={{ cursor: 'pointer', color: 'var(--accent-orange)', fontWeight: '600' }}
                        >
                            Back to top ↑
                        </span>
                    </div>
                </motion.div>
            </div>

            <Footer />
        </div>
    );
};

export default RefundPolicy;
